<template>
  <el-dialog
    id="profile-dialog"
    ref="baseball"
    top="10vh"
    width="64rem"
    :center="true"
    :show-close="true"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    :visible.sync="showProfileDialog"
    :destroy-on-close="true"
    :append-to-body="true"
  >
    <div id="profile" style="">
      <div id="header">
        <div style="padding-top: 10%; text-align:center;">
          <el-image id="image" :src="imageSrc" :fit="'cover'" style="border-radius: 15px">
            <div slot="error" class="image-slot">
              <i class="el-icon-picture-outline"></i>
            </div>
          </el-image>

          <h2>{{ initialProfile.identifier }}</h2>
        </div>

        <el-tabs id="visible-tabs" :stretch="true" v-model="activeName">
          <el-tab-pane label="Demographics" name="demographics"></el-tab-pane>
          <el-tab-pane label="Campaigns & Surveys" name="campaigns"></el-tab-pane>
          <el-tab-pane label="Interactions" name="interactions"></el-tab-pane>
          <el-tab-pane label="Opt-in & Permission" name="optin"></el-tab-pane>
        </el-tabs>
      </div>

      <div id="hidden-tabs">
        <el-tabs v-model="activeName">
          <el-tab-pane label="Demographics" name="demographics">
            <demographics-tab
              :loading="$apollo.queries.profile.loading"
              :demographics="demographics"
              @blacklist="handleBlacklistNugget"
              @remove="handleRemoveNugget"
            />
          </el-tab-pane>
          <el-tab-pane label="Campaigns & Surveys" name="campaigns">
            <campaigns-and-surveys-tab
              :loading="$apollo.queries.profile.loading"
              :campaigns="campaigns"
            />
          </el-tab-pane>
          <el-tab-pane label="Interactions" name="interactions">
            <interactions-tab
              :loading="$apollo.queries.profile.loading"
              :interactions="processedInteractions"
            />
          </el-tab-pane>
          <el-tab-pane label="Opt-in" name="optin">
            <optins-tab :loading="$apollo.queries.profile.loading" :optins="optins" />
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { get } from 'lodash';
import collect from 'collect.js';
import jdenticon from 'jdenticon';
import profileQuery from '@/graphql/profiles/profileQuery.gql';
import blacklistNuggetMutation from '@/graphql/profiles/pro/blacklistNuggetMutation.gql';
import removeNuggetMutation from '@/graphql/profiles/pro/removeNuggetMutation.gql';
import DemographicsTab from '@/components/profiles/profile/tabs/DemographicsTab';
import CampaignsAndSurveysTab from '@/components/profiles/profile/tabs/CampaignsTab';
import InteractionsTab from '@/components/profiles/profile/tabs/InteractionsTab';
import OptinsTab from '@/components/profiles/profile/tabs/OptinsTab';

export default {
  name: 'ProfileBaseballCard',
  components: {
    CampaignsAndSurveysTab,
    DemographicsTab,
    InteractionsTab,
    OptinsTab,
  },
  apollo: {
    profile: {
      client: 'apiv2',
      query: profileQuery,
      skip: true,
      fetchPolicy: 'network-only',
      variables: {
        id: this.id,
      },
    },
  },
  data: () => ({
    activeName: 'demographics',
    activeTab: 'demographics',
    defaultOptins: [
      { channel: 'Email', opt_in: false, opt_out: false, permission: false, date_opted_in: false },
      { channel: 'SMS', opt_in: false, opt_out: false, permission: false, date_opted_in: false },
      { channel: 'WhatsApp', opt_in: false, opt_out: false, permission: false, date_opted_in: false },
      { channel: 'Telegram', opt_in: false, opt_out: false, permission: false, date_opted_in: false },
    ],
  }),

  computed: {
    demographics() {
      return this.profile ? this.profile.demographics : [];
    },
    campaigns() {
      return get(this.profile, 'campaigns', []);
    },
    processedInteractions() {
      const interactions = get(this.profile, 'interactions', []);
      return interactions.map(interaction => interaction);
    },
    optins() {
      return get(this.profile, 'optins', []);
    },
    panelSettings() {
      return {
        uuid: `profile-panel-${this.panelId}`,
        type: 'profile',
        settings: {
          name: 'Recent Messages',
          campaign_ids: [this.id],
        },
      };
    },

    panelId() {
      const length = 5;
      return Math.round(36 ** (length + 1) - Math.random() * 36 ** length)
        .toString(36)
        .slice(1);
    },
    imageSrc() {
      if (this.initialProfile.image) return this.initialProfile.image;
      const identiconToBase64 = btoa(jdenticon.toSvg(this.initialProfile.identifier, 128));
      return `data:image/svg+xml;base64,${identiconToBase64}`;
    },
    showProfileDialog: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('close', val);
      },
    },
  },
  props: {
    initialProfile: {
      type: Object,
      required: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    get,
    handleOpen() {
      this.$apollo.queries.profile.setVariables({
        id: this.initialProfile.id,
      });
      this.$apollo.queries.profile.skip = false;
    },
    handleBlacklistNugget(nugget) {
      const blacklistNugget = {
        profile_id: this.initialProfile.id,
        field: nugget.field,
        text: nugget.value,
      };

      this.$apollo
        .mutate({
          mutation: blacklistNuggetMutation,
          variables: { nugget: blacklistNugget },
          client: 'apiv2',
        })
        .then(() => {
          this.$apollo.queries.profile.refetch();
        })
        .catch(() =>
          this.$notify({
            title: 'Error',
            message: 'Failed to blacklist nugget',
            type: 'error',
          }),
        );
    },
    handleRemoveNugget(nugget) {
      const nuggetToRemove = {
        profile_id: this.initialProfile.id,
        field_key: nugget.field,
        value: nugget.value,
        category: 'demographics',
      };

      this.$apollo
        .mutate({
          mutation: removeNuggetMutation,
          variables: { nugget: nuggetToRemove },
          client: 'apiv2',
        })
        .then(() => {
          this.$apollo.queries.profile.refetch();
        })
        .catch(() =>
          this.$notify({
            title: 'Error',
            message: 'Failed to remove nugget',
            type: 'error',
          }),
        );
    },
    filterByChannelType(interactions) {
      const filteredInteractions = collect(interactions)
        .whereIn('channel', this.filteredChannelKeys)
        .all();

      return filteredInteractions.length ? filteredInteractions : interactions;
    },
  },
  mounted() {
    this.handleOpen();
  },
};
</script>

<style lang="scss">
#profile {
  height: 82rem;

  #header {
    height: 45%;
    position: relative;
    padding: 0px 20px;

    #image {
      width: 150px;
      height: 150px;
    }

    h2 {
      margin-top: 15px;
    }
  }
}

#visible-tabs {
  position: absolute;
  bottom: 0;

  .el-tabs__item:focus.is-active.is-focus:not(:active) {
    box-shadow: none;
  }

  .el-tabs__nav-wrap::after {
    height: 0px;
    display: none;
  }

  .el-tabs__active-bar .is-top {
    display: none;
  }

  .el-tabs__header {
    margin: 0px;
  }
}

#hidden-tabs {
  height: 55%;
  background-color: #f7f8fc;
  padding: 5px 5px;
  overflow-y: hidden;

  .el-tabs__header .is-top {
    display: none;
  }
}

#profile-dialog {
  .el-dialog--center .el-dialog__body {
    padding: 5px 0px 0px;
  }
}
</style>



// WEBPACK FOOTER //
// DialogCard.vue?aa54712c